import T from '../../common/components/T';
import { Img, Portal, Tooltip, svgSprite } from '../../hybrid/wrappers';
import css from './ModalInfoOffice.module.scss';
import classNames from 'classnames';
import UserPersonalInfo from './components/UserPersonalInfo/UserPersonalInfo';
import Skeleton from '../plug/Skeleton/Skeleton';
import { createStats, createVolumes, getStrokeWidth, partnerStatsSkeleton, progressPattern, skeleton } from './helpers';
import { useContext, useEffect, useMemo, useState } from 'react';
import UserContext from '../../common/utils/getContext';
import { getData } from '../../common/utils/router';
import ProgressBar from '../progressBarGT/ProgressBar';
import { rankColors } from '../../project/uikit/data-variables/rank-colors';
import { useWindowDimensions } from '../../common/utils/windowDimensions';

const RankBlock = (props) => {
    const { userData, career } = props;

    const width = useWindowDimensions();
    const strokeWidth = useMemo(() => getStrokeWidth(width), [width]);

    const rankColor = useMemo(() => {
        return rankColors[career?.rank?.id] || rankColors[rankColors.length - 1];
    }, [career]);
    return (
        <div className={css.progressItemWrapper}>
            <div className={css.progressItem} id={'userRankModalWrapper'}>
                <ProgressBar
                    config={{
                        percent: +career?.percent,
                        strokeWidth,
                        id: 'userRankModal',
                        defaultColor: 'rgba(177, 138, 75, 0.10)',
                        progressColor: [rankColor['gradientColor1'], rankColor['gradientColor2']]
                    }}
                />
                <div className={css.progressItemInfo}>
                    <div className={css.progressItemIcon}>
                        <Img
                            className={classNames(css.progressItemIconSvg, `rank${userData?.rank?.id || 0}`)}
                            src={`/src/img/ranks/rank${career?.rank?.id || 0}.png`}
                            alt={`Rank ${career?.rank?.id || 0}`}
                        />
                    </div>
                    <p className={css.progressItemLabel}>
                        <T textName='current-rank' defaultStr='Текущий ранг' />
                    </p>
                    <p className={css.progressItemValue}>{career?.rank?.name || '-'}</p>
                </div>
            </div>
            <div className={css.underProgress}>
                <p className={css.underProgressLabel}>
                    <T textName='next-rank' defaultStr='Следующий ранг' />
                </p>
                <p className={css.underProgressValue}>{career?.nextRank?.name || '-'}</p>
            </div>
        </div>
    );
};

const ActivityBlock = (props) => {
    const { activity } = props;
    const width = useWindowDimensions();
    const strokeWidth = useMemo(() => getStrokeWidth(width), [width]);
    const activityCondition = activity?.conditions?.activity;
    const activityTarget = activity?.conditions?.total_personal_volume + activity?.conditions?.need_personal_volume;

    const percent =
        activityCondition?.personal_volume?.target <= activityCondition?.personal_volume?.value && activity?.activity
            ? 100
            : (activityCondition?.personal_volume?.value / activityCondition?.personal_volume?.target) * 100;
    const transformActivityDate = (date) => {
        return date?.split(' ')[0];
    };
    return (
        <div className={css.progressItemWrapper}>
            <div className={css.progressItem} id={'userActivityModalWrapper'}>
                <ProgressBar
                    config={{
                        percent,
                        strokeWidth,
                        id: 'userActivityModal',
                        defaultColor: 'rgba(177, 138, 75, 0.10)',
                        progressColor: activity?.activity
                            ? ['rgba(122, 166, 67, 1)', 'rgba(51, 80, 16, 1)']
                            : ['rgba(201, 201, 201, 1)', 'rgba(72, 72, 72, 1)']
                    }}
                />
                <div className={css.progressItemInfo}>
                    <div className={css.progressItemIcon}>
                        <Img
                            className={classNames(css.progressItemIconSvg, 'activityShadow')}
                            src={`/src/img/activity/${activity?.activity ? 'activity-done' : 'activity-fail'}.png`}
                            alt={'Activity'}
                        />
                    </div>
                    <p className={css.progressItemLabel}>
                        <T textName='activity' defaultStr='Активность' />
                    </p>
                    <p className={css.progressItemValue}>
                        {activity?.activity ? (
                            <T textName='completed' defaultStr='Выполнена' />
                        ) : (
                            <T textName='not-completed' defaultStr='Не выполнена' />
                        )}
                    </p>
                    <p className={css.infoDate}>
                        {activity?.activity ? (
                            <T
                                vars={{ end: transformActivityDate(activityCondition?.activity_date) }}
                                textName='activity-activity_date-to-general'
                                defaultStr='по ${end}'
                            />
                        ) : activityCondition?.activity_date ? (
                            <T
                                vars={{ end: transformActivityDate(activityCondition?.activity_date) }}
                                textName='activity-activity_date-from-general'
                                defaultStr='с ${end}'
                            />
                        ) : (
                            ''
                        )}
                    </p>
                </div>
            </div>
            <div className={css.underProgress}>
                <p className={css.underProgressLabel}>
                    <T textName='personal-volume-pv' defaultStr='Личный объем, PV' />
                </p>
                <p className={css.underProgressValue}>
                    {activityTarget === activity?.conditions?.total_personal_volume ? (
                        <>{activityTarget}</>
                    ) : (
                        <>
                            {activityCondition?.personal_volume?.value || '0'} <T textName='of' defaultStr='из' />{' '}
                            {activityCondition?.personal_volume?.target || '0'}
                        </>
                    )}
                </p>
            </div>
        </div>
    );
};

const PartnerStats = (props) => {
    const { stats } = props;
    return (
        <div className={css.statistics}>
            <p className={css.modalSubtitle}>
                <T textName='stats-by-partners-by-all-period' defaultStr='Статистика по партнерам за весь период' />
            </p>
            <div className={css.partnerStats}>
                {stats?.map((item) => (
                    <div key={item?.icon} className={css.statsItem}>
                        <div className={css.statsItemIcon}>{svgSprite(item.icon, { className: css.statsItemSvg })}</div>
                        <div className={css.statsItemInfo}>
                            <p className={css.statsItemName}>
                                {item.name}
                                {item.hint ? (
                                    <Tooltip compStyle={{ inner: { display: 'flex' } }} text={item.hint}>
                                        {svgSprite('question-circle-fill', { className: 'tooltipShadow' })}
                                    </Tooltip>
                                ) : null}
                            </p>
                            <p className={css.statsItemValue}>
                                {Array.isArray(item.value)
                                    ? item.value?.map((elem) => (
                                          <span className={css.statsItemValues} key={Math.random()}>
                                              {elem}
                                          </span>
                                      ))
                                    : item.value}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const RenderData = (props) => {
    const { userData, closeHandler } = props;
    const { userToken, lang } = useContext(UserContext);
    const [partnerStats, setPartnerStats] = useState(null);
    const [career, setCareer] = useState(null);
    const [activity, setActivity] = useState(null);
    const stats = useMemo(() => createStats(partnerStats), [partnerStats]);
    const volumes = useMemo(() => createVolumes(userData?.marketing_profile), [userData?.marketing_profile]);

    useEffect(() => {
        if (userData?.marketing_profile?.node_id) {
            getData(`/api/v1/user/marketing/node-statistic/${userData?.marketing_profile?.node_id}`, userToken, lang).then(
                (response) => {
                    if (response?.data) {
                        setPartnerStats(response?.data?.[0]);
                    }
                }
            );
        }
    }, [userToken, lang, userData]);

    useEffect(() => {
        if (!userData?.id) return;
        getData(`/api/v1/user/career/${userData?.id}`, userToken, lang).then((response) => {
            if (response?.data) {
                setCareer(response?.data);
            }
        });
    }, [userToken, lang, userData]);

    useEffect(() => {
        getData(`/api/v1/user/marketing/activity-progress/${userData?.id}`, userToken, lang).then((response) => {
            if (response?.data) {
                setActivity(response?.data);
            }
        });
    }, [userToken, lang, userData]);

    return (
        <div onClick={(e) => e.stopPropagation()} className={css.modal}>
            <div onClick={closeHandler} className={classNames(css.modalClose, 'grow')}>
                {svgSprite('cross', { className: css.modalCloseSvg })}
            </div>
            <p className={css.title}>
                <T textName='info-about-selected-user' defaultStr='Информация о выбранном пользователе' />
            </p>
            <div className={css.top}>
                <div className={css.blockItem}>
                    <UserPersonalInfo userData={userData} />
                </div>
                {userData?.sponsor ? (
                    <div className={classNames(css.blockItem, css.sponsorBlock)}>
                        <UserPersonalInfo userData={userData?.sponsor} isSponsor={true} />
                    </div>
                ) : null}
            </div>
            <Skeleton wait={!!stats} Component={PartnerStats} pattern={partnerStatsSkeleton} componentProps={{ stats }} />
            <div className={css.bottom}>
                <div className={css.botItem}>
                    <p className={css.modalSubtitle}>
                        <T textName='marketing-volumes' defaultStr='Маркетинговые объемы' />
                    </p>
                    <div className={css.volumes}>
                        {volumes?.map((volume) => (
                            <div key={volume.icon} className={css.volume}>
                                <div className={css.volumeIcon}>{svgSprite(volume.icon, { className: css.volumeIconSvg })}</div>
                                <p className={css.volumeName}>{volume.name}</p>
                                <p className={css.volumeValue}>{volume.value}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={css.botItem}>
                    <p className={css.modalSubtitle}>
                        <T textName='rank-activity-progress' defaultStr='Прогресс по рангу и активности' />
                    </p>
                    <div className={css.progress}>
                        <Skeleton
                            pattern={progressPattern}
                            wait={!!career}
                            Component={RankBlock}
                            componentProps={{ userData, career }}
                            wrapperStyles={css.progressSkeleton}
                        />
                        <Skeleton
                            pattern={progressPattern}
                            wait={!!activity}
                            Component={ActivityBlock}
                            componentProps={{ activity }}
                            wrapperStyles={css.progressSkeleton}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function ModalInfoOffice(props) {
    const { userData, closeHandler } = props;
    return (
        <Portal closeHandler={closeHandler}>
            <Skeleton
                Component={RenderData}
                componentProps={props}
                wait={!!userData}
                pattern={skeleton}
                wrapperStyles={css.skeleton}
            />
        </Portal>
    );
}
